import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { existsSync, readFileSync, statSync } from 'node:fs';
import { extname } from 'node:path';
import { fetchWithTimeout, unwrapDataResponse } from '@wport/core';
import { InvalidArgumentError, NetworkError } from '../../../lib/errors';
import { enterprisePost } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { printJson } from '../../../lib/output';
import type { WriteCtx } from '../jobs/write-shared';
import type { EnterpriseCompany } from './types';

/**
 * 允許的 logo 副檔名 → MIME type（鏡射後端
 * `ALLOWED_FILE_TYPES.COMPANY_LOGO.MIME_TYPES`，src/common/constants/file-upload.constants.ts）。
 * 這支 CLI 沒有任何 MIME-detection 套件依賴（package.json 僅有 cli-table3/commander/env-paths/
 * openapi-fetch/picocolors），為了 3 筆對應關係不值得新增依賴，改用 `path.extname()` 做本地判斷。
 */
const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
	'.png': 'image/png',
	'.jpg': 'image/jpeg',
	'.jpeg': 'image/jpeg',
};

/** 後端 `FILE_UPLOAD_LIMITS.COMPANY_LOGO_MAX_SIZE`（2MB）。 */
const COMPANY_LOGO_MAX_SIZE = 2 * 1024 * 1024;

interface LocalFileInfo {
	contentType: string;
	fileSize: number;
	bytes: Buffer;
}

/**
 * 本地檔案檢查（存在／是檔案／副檔名合法／大小合法），全部在任何網路呼叫之前完成。
 *
 * 大小下限採 `size > 0`（拒絕 0 byte 檔案），對齊後端 DTO 的 `@Min(1)`（見
 * `EnterpriseCompanyLogoPresignDto.file_size`）——brief 寫「1..2MB」不是字面上要求至少
 * 1MB，而是「大於 0、最多 2MB」。
 */
function inspectLocalFile(path: string): LocalFileInfo {
	if (!existsSync(path)) {
		throw new InvalidArgumentError(`File not found: ${path}`);
	}
	const stat = statSync(path);
	if (!stat.isFile()) {
		throw new InvalidArgumentError(`Not a regular file: ${path}`);
	}

	const ext = extname(path).toLowerCase();
	const contentType = EXTENSION_TO_CONTENT_TYPE[ext];
	if (!contentType) {
		throw new InvalidArgumentError(
			`Unsupported file extension "${ext || '(none)'}" — allowed: ${Object.keys(EXTENSION_TO_CONTENT_TYPE).join(', ')}`
		);
	}

	if (stat.size <= 0) {
		throw new InvalidArgumentError(`File is empty: ${path}`);
	}
	if (stat.size > COMPANY_LOGO_MAX_SIZE) {
		throw new InvalidArgumentError(`File too large: ${stat.size} bytes (max ${COMPANY_LOGO_MAX_SIZE} bytes / 2MB)`);
	}

	const bytes = readFileSync(path);
	return { contentType, fileSize: stat.size, bytes };
}

interface LogoPresignResponse {
	upload_url: string;
	s3_key: string;
	expires_in: number;
}

interface LogoConfirmResponse {
	logo_url: string;
	company: EnterpriseCompany;
}

export interface LogoUploadIdempotencyFlags {
	/**
	 * 基底 key；presign／confirm 各自衍生獨立實際 key（`${base}-presign` / `${base}-confirm`），
	 * 不直接把同一個字面值重複用在兩次呼叫上——即使 presign→PUT→confirm 是同一次使用者操作，
	 * 兩次 POST 打的是不同 endpoint、不同 payload，沿用 Task 3 review 對「同一把 key 打兩個不同
	 * payload」的疑慮（見 update.ts `resolveUpdateIdempotencyKeys` 的判斷）。不給旗標則各自
	 * fresh `randomUUID()`。
	 */
	idempotencyKey?: string;
}

function resolveLogoIdempotencyKeys(flags: LogoUploadIdempotencyFlags): { presignKey: string; confirmKey: string } {
	if (flags.idempotencyKey) {
		return {
			presignKey: `${flags.idempotencyKey}-presign`,
			confirmKey: `${flags.idempotencyKey}-confirm`,
		};
	}
	return { presignKey: randomUUID(), confirmKey: randomUUID() };
}

/** 印出最終結果：JSON 印整份 confirm 回應（`{ logo_url, company }`）；文字只印一行。 */
function printLogoResult(result: LogoConfirmResponse, format: 'table' | 'json'): void {
	if (format === 'json') {
		printJson(result);
		return;
	}
	process.stdout.write(`Updated logo: ${result.logo_url}\n`);
}

/**
 * `company logo upload <path>` 核心（pm_41 Card B Task 4）：
 * 本地檔案檢查 → `POST /company/logo/presign` → PUT bytes 直傳 S3 presigned URL →
 * `POST /company/logo/confirm` → 印結果。
 *
 * S3 PUT 刻意不經過 `enterprisePost`／`enterpriseGet`／`enterprisePatch`：那組 helper 是打
 * `/api/v1/enterprise` API server，presigned URL 是完全不同的 host（S3 bucket），改用
 * `fetchWithTimeout`（`api-client.ts`）直接組 `Request` 送出，檔案位元組完全不進 API server。
 */
export async function runCompanyLogoUpload(
	ctx: WriteCtx,
	apiKey: string,
	path: string,
	idempotencyFlags: LogoUploadIdempotencyFlags
): Promise<void> {
	const { contentType, fileSize, bytes } = inspectLocalFile(path);
	const { presignKey, confirmKey } = resolveLogoIdempotencyKeys(idempotencyFlags);

	const requestOpts = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey };

	const { body: presignBody } = await enterprisePost(
		requestOpts,
		'/company/logo/presign',
		{ content_type: contentType, file_size: fileSize },
		{ idempotencyKey: presignKey }
	);
	const presign = unwrapDataResponse<LogoPresignResponse>(presignBody);

	const putRequest = new Request(presign.upload_url, {
		method: 'PUT',
		headers: { 'Content-Type': contentType },
		body: bytes,
	});
	const putResponse = await fetchWithTimeout(putRequest, ctx.timeoutMs);
	if (!putResponse.ok) {
		// S3 上傳失敗不是我們自己 API 的 4xx（那走 ServerClientHttpError/exit 3），也不是單純的
		// DNS/timeout 網路錯（那走 fetchWithTimeout 內建分類）；歸類為 NetworkError（exit 4）——
		// 對呼叫端而言同樣是「上游基礎設施沒能完成」，且與 unwrapDataResponse 的
		// ServerOrNetworkError 用途一致（後端契約 / 上游依賴異常，非使用者輸入錯）。
		throw new NetworkError(`Failed to upload file to S3: HTTP ${putResponse.status}`);
	}

	const { body: confirmBody } = await enterprisePost(
		requestOpts,
		'/company/logo/confirm',
		{ s3_key: presign.s3_key },
		{ idempotencyKey: confirmKey }
	);
	const result = unwrapDataResponse<LogoConfirmResponse>(confirmBody);
	printLogoResult(result, ctx.format);
}

interface LogoUploadFlags {
	idempotencyKey?: string;
}

/**
 * `company logo` 註冊（pm_41 Card B Task 4）。
 *
 * `logo` 本身是命令群組（非直接掛 `<file>` 引數），底下掛 `upload <path>` 子命令，對齊
 * plan 的字面命令介面 `wport enterprise company logo upload <path>`（Task 0 scaffolding
 * 原本把 `logo` 寫成直接吃 `<file>` 引數，這裡改成群組 + 子命令，做法比照
 * `jobs`：group + 多個子命令，見 `../jobs/index.ts`）。目前只有一個子命令，故直接在本檔
 * inline 註冊，不另開 `logo/` 目錄。
 */
export function registerEnterpriseCompanyLogo(parent: Command): void {
	const logo = parent.command('logo').description('Manage your company logo');
	logo
		.command('upload <path>')
		.description('Upload and set your company logo (presign + upload + confirm)')
		.option('--idempotency-key <key>', 'base key for presign/confirm (each derives its own; default: fresh UUIDs)')
		.action(async (path: string, flags: LogoUploadFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runCompanyLogoUpload(ctx, key, path, { idempotencyKey: flags.idempotencyKey });
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { inspectLocalFile, resolveLogoIdempotencyKeys };
